import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Divider, Group, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconMapPin, IconWheat } from '@tabler/icons-react';
import OfferLotCard from '@/components/app/sell/OfferLotCard';
import { useExchangeSession, useExchangeStatuses, useSaleOffersByBasis, useSellOfferActions } from '@/hooks/useSell';
import { isVisibleInSell } from '@/lib/sellOffer';
import { makeSellEditUrl, makeSellOfferUrl } from '@/lib/sellLinks';
import { isServiceUnavailable } from '@/api/errors';
import type { SellBasisGroup } from '@/lib/sellTree';
import type { MyBestOffer } from '@/types/bestPrice';
import { formatOrgName } from '@/lib/format';

interface SellBasisCardProps {
  readonly basis: SellBasisGroup;
  /** Фильтр по культуре (родительский тип); null — все культуры базиса. */
  readonly good?: string | null;
  /** Лучшая встречная цена по моему офферу (id → MyBestOffer) — для цены/«Продать» на карточке. */
  readonly bestOffers?: ReadonlyMap<number, MyBestOffer>;
}

// Карточка базиса «Продам»: шапка (точка погрузки) + лоты сразу карточками, сгруппированные по культуре.
// Клик по лоту → детали моего предложения (без промежуточной страницы-группы). Лоты грузятся по базису
// одним запросом; действия (пауза/возобновление/удаление) и лучшая цена живут на самой карточке лота.
export default function SellBasisCard({ basis, good, bestOffers }: SellBasisCardProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useSaleOffersByBasis(basis.address, basis.addressTitle);
  const actions = useSellOfferActions();

  // Культуры базиса с видимыми лотами (ACTIVE/SUSPENDED), с учётом фильтра по культуре.
  const cultures = useMemo(
    () =>
      (data ?? [])
        .filter((group) => !good || group.parentGoodsType === good)
        .map((group) => ({ group, lots: (group.orders ?? []).filter(isVisibleInSell) }))
        .filter((entry) => entry.lots.length > 0),
    [data, good],
  );

  const totalLots = cultures.reduce((sum, entry) => sum + entry.lots.length, 0);

  // Биржевые статусы подтягиваем только для биржевых лотов этого базиса (одним запросом); торговое окно —
  // общий запрос (кэшируется между карточками). Для внебиржевых офферов запрос не делаем.
  const exchangeIds = useMemo(
    () => cultures.flatMap((entry) => entry.lots).filter((lot) => lot.toExchange).map((lot) => lot.id),
    [cultures],
  );
  const { data: exchangeStatuses, isLoading: statusesLoading } = useExchangeStatuses(exchangeIds);
  const { data: session } = useExchangeSession();
  // Статусы «готовы», если запрашивать нечего или запрос завершился: до этого биржевые действия не разрешаем.
  const exchangeReady = exchangeIds.length === 0 || !statusesLoading;

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
          <ThemeIcon variant="light" color="brand" size={40} radius="md">
            <IconMapPin size={22} />
          </ThemeIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fw={700} truncate title={formatOrgName(basis.addressTitle)}>
              {formatOrgName(basis.addressTitle)}
            </Text>
            <Text size="sm" c="gray.7" truncate title={basis.address}>
              {basis.address}
            </Text>
          </Stack>
        </Group>
        {totalLots > 0 ? (
          // Счётчик объектов — НЕЙТРАЛЬНЫЙ серый (не зелёный): количество, а не статус (таксономия бейджей).
          <Badge variant="light" color="gray" tt="none" style={{ flexShrink: 0 }}>
            {t('app.sell.lotsCount', { count: totalLots })}
          </Badge>
        ) : null}
      </Group>

      <Divider my="sm" />

      {isError ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          <Group justify="space-between" wrap="nowrap">
            <Text>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
            <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </Group>
        </Alert>
      ) : isLoading ? (
        <Stack gap="sm">
          <Skeleton h={96} />
        </Stack>
      ) : cultures.length === 0 ? (
        <Text size="sm" c="gray.6">
          {t('app.sell.basisEmpty')}
        </Text>
      ) : (
        <Stack gap="lg">
          {cultures.map(({ group, lots }) => (
            <Stack gap="sm" key={`${group.parentGoodsType}|${group.year}`}>
              <Group gap={6} wrap="nowrap">
                <IconWheat size={18} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
                <Text fw={600}>{group.parentGoodsType}</Text>
              </Group>
              {lots.map((offer) => (
                <OfferLotCard
                  key={offer.id}
                  offer={offer}
                  to={makeSellOfferUrl({
                    orderId: offer.id,
                    address: basis.address,
                    addressTitle: basis.addressTitle,
                    goods: group.parentGoodsType ?? offer.goodsType ?? '',
                    year: group.year ?? offer.cropYear ?? 0,
                  })}
                  editTo={makeSellEditUrl(offer.id)}
                  bestOffer={bestOffers?.get(offer.id)}
                  exchangeInfo={exchangeStatuses?.[offer.id]}
                  exchangeReady={exchangeReady}
                  session={session}
                  onSuspend={() => actions.suspend.mutate(offer.id)}
                  onUnsuspend={() => actions.unsuspend.mutate(offer.id)}
                  onDelete={() => actions.confirmDeleteOffer(offer.id)}
                  onPublishExchange={() => actions.publishExchange.mutate(offer.id)}
                  onRemoveExchange={() => actions.confirmRemoveFromExchange(offer.id)}
                  pending={actions.pendingId(offer.id)}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
}
